import React, { useState, FormEvent } from 'react';
import { Mail, Phone, Send } from 'lucide-react';
import { db } from '../firebaseConfig'; // Adjust the import path to your Firebase config file
import { collection, addDoc } from 'firebase/firestore';
// import { Turnstile } from '@marsidev/react-turnstile'; // --- 1. COMMENTED OUT
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { contactForm } from '../services';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

const ContactPage: React.FC = () => {
  const { t } = useTranslation(['contact', 'seo']);
  const isGlobal = typeof window !== 'undefined' && (window as any).__isIndianLocale__ === false;
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<string | null>(null);
  // const [turnstileToken, setTurnstileToken] = useState<string | null>(null); // --- 2. COMMENTED OUT
  // const siteKey = import.meta.env.VITE_TURNSTILE_SITEKEY as string; // --- COMMENTED OUT

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return t('contact:validation.name_required');
    if (name.trim().length < 2) return t('contact:validation.name_min_length');
    if (name.trim().length > 100) return t('contact:validation.name_max_length');
    if (!/^[a-zA-Z\s.'-]+$/.test(name)) return t('contact:validation.name_invalid_chars');
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return t('contact:validation.email_required');
    // Standard email validation pattern
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) return t('contact:validation.email_invalid');
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (!phone) return undefined; // Phone is optional
    // Allow numbers, spaces, hyphens, parentheses, and plus sign
    const phoneRegex = /^[0-9+\s().-]{7,20}$/;
    if (!phoneRegex.test(phone)) return t('contact:validation.phone_invalid');
    return undefined;
  };

  const validateMessage = (message: string): string | undefined => {
    if (!message.trim()) return t('contact:validation.message_required');
    if (message.trim().length < 10) return t('contact:validation.message_min_length');
    if (message.trim().length > 1000) return t('contact:validation.message_max_length');
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      message: validateMessage(formData.message),
    };

    setErrors(newErrors);
    // Form is valid if there are no defined errors
    return !Object.values(newErrors).some(error => error !== undefined);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      toast.error(t('contact:form.validation_error'));
      return;
    }

    // --- 3. COMMENTED OUT Turnstile Check ---
    // if (!turnstileToken) {
    //   toast.error('Please complete the challenge');
    //   return;
    // }

    setLoading(true);
    setSubmissionStatus(null); // Reset status

    try {
      // Sanitize inputs before sending
      const sanitizedData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || t('contact:form.phone_not_provided'), // Optional field
        message: formData.message.trim()
      };

      await contactForm(sanitizedData);

      setSubmissionStatus(t('contact:form.success_message'));
      toast.success(t('contact:form.success_toast'));
      
      // Reset form after successful submission
      setFormData({ name: '', email: '', phone: '', message: '' });
      // setTurnstileToken(null); // --- COMMENTED OUT
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus(t('contact:form.error_message'));
      toast.error(t('contact:form.error_toast'));
    } finally {
      setLoading(false);
    }
  };

  // --- 4. COMMENTED OUT Turnstile Handler ---
  // const handleTurnstileSuccess = (token: string) => {
  //   setTurnstileToken(token);
  // };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear the specific error when the user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title={t('seo:contact.title', { defaultValue: 'Contact Sarvalok | Support & Inquiries' })}
        description={t('seo:contact.description', { defaultValue: 'Reach out to Sarvalok for support, inquiries, or feedback. Connect with our team for assistance with your cultural research and spiritual study needs.' })}
        url="/contact-us"
      />
      <div className="sl-contact-page relative overflow-hidden min-h-screen bg-white text-gray-800 py-12 px-4">
      <div
        aria-hidden="true"
        className="sl-contact-torana-ornament pointer-events-none absolute right-4 top-0 hidden lg:block xl:right-10"
      >
        <img
          src="/verticaltorana.jpg"
          alt=""
          className="h-[368px] w-[84px] object-contain xl:h-[448px] xl:w-[102px]"
        />
      </div>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <h1 className="text-4xl font-bold text-center mb-8 text-black dark:text-white">{t('contact:page_title')}</h1>
        <p className="text-center text-lg mb-12">
          {t('contact:page_description')}
        </p>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-black dark:text-white">
              {t('contact:form.title')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  {t('contact:form.name_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  placeholder={t('contact:form.name_placeholder')}
                  maxLength={100}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  {t('contact:form.email_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                  placeholder={t('contact:form.email_placeholder')}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-1">
                  {t('contact:form.phone_label')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 ${
                    errors.phone ? 'border-red-500' : ''
                  }`}
                  placeholder={t('contact:form.phone_placeholder')}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  {t('contact:form.message_label')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 ${
                    errors.message ? 'border-red-500' : ''
                  }`}
                  placeholder={t('contact:form.message_placeholder')}
                  maxLength={1000}
                />
                {errors.message && (
                  <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/1000 {t('contact:form.characters')}
                </p>
              </div>

              {/* --- 5. COMMENTED OUT Turnstile Component --- */}
              {/* <div className="mt-4">
                <Turnstile
                  siteKey={siteKey}
                  onSuccess={handleTurnstileSuccess}
                  options={{
                    theme: 'light',
                    size: 'normal',
                  }}
                />
              </div> */}

              <button
                type="submit"
                className="w-full bg-black text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <Send size={20} />
                    {t('contact:form.send_button')}
                  </>
                )}
              </button>

              {/* Submission Status */}
              {submissionStatus && (
                <p
                  className={`text-sm mt-2 text-center ${
                    submissionStatus.includes(t('contact:form.success_message'))
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {submissionStatus}
                </p>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold mb-6 text-black dark:text-white">{t('contact:info.title')}</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail size={24} className="text-black dark:text-gray-300" />
                  <a
                    href="mailto:support@sarvalok.com"
                    className="hover:underline text-black dark:text-gray-300 dark:hover:text-white"
                  >
                    {t('contact:info.support_email')}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={24} className="text-black dark:text-gray-300" />
                  <a href={isGlobal ? "tel:+16504601492" : "tel:+918125831103"} className="hover:underline text-black dark:text-gray-300 dark:hover:text-white">
                    {t(isGlobal ? 'contact:info.phone_global' : 'contact:info.phone')}
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 text-black">{t('contact:about.title')}</h3>
              <p className="text-sm">
                {t('contact:about.description')}
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 text-black">{t('contact:press.title')}</h3>
              <div className="flex items-center gap-3">
                <Mail size={24} className="text-black" />
                <a
                  href="mailto:press@sarvalok.com"
                  className="hover:underline text-black"
                >
                  {t('contact:press.email')}
                </a>
              </div>
            </div>
            <div>
              <img style={{width:"200px"}} src="/contact.png"/>
            </div>
          </div>
        </div>
      </div>
      </div>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default ContactPage;
